<template>
  <div class="ios-glass-page">
    <div class="background-blobs">
      <div class="blob blob-1"></div>
      <div class="blob blob-2"></div>
      <div class="blob blob-3"></div>
    </div>

    <!-- 设备选择卡片 -->
    <div class="glass-container">
      <div class="device-card" @click="openDevicePopup">
        <div class="device-icon" :class="{ online: mqttConnected }">
          <van-icon name="tv-o" size="22" color="#fff" />
        </div>
        <div class="device-content">
          <div class="device-name">{{ deviceData.remark || '请点击选择设备' }}</div>
          <div class="device-status">
            <span :class="mqttConnected ? 'text-green' : 'text-gray'">
              {{ connStatusText }}
            </span>
            <template v-if="deviceData.ip">
              <span class="divider">|</span>
              <span>{{ deviceData.ip }}</span>
              <span v-if="deviceData.enableLocalHttp" class="http-tag">HTTP</span>
            </template>
          </div>
        </div>
        <van-icon name="arrow" class="arrow-btn" />
      </div>
    </div>

    <!-- 受控模式下的主操作区 -->
    <template v-if="phase !== 'idle'">
      <!-- 项目与时长 -->
      <div class="glass-container">
        <h3 class="panel-title">测试配置</h3>
        <van-cell title="运动项目" :value="currentProjectName" is-link :disabled="configLocked"
                  @click="showProjectPopup = true" />
        <van-cell title="测试时长" :value="durationText" is-link :disabled="configLocked"
                  @click="showDurationPopup = true" />
      </div>

      <!-- 工位列表 -->
      <div class="glass-container">
        <h3 class="panel-title">
          上场工位（已就位 {{ readySlotCount }}/{{ slots.length }} 人）
          <span v-if="!configLocked" class="panel-action" @click="openStudentPicker">选人上场</span>
        </h3>

        <div v-if="slots.length === 0" class="empty-slots" @click="openStudentPicker">
          <van-icon name="plus" size="20" />
          <span>点击选择考生并分配工位</span>
        </div>

        <div v-else class="slot-grid">
          <div v-for="s in slots" :key="s.slot" class="slot-card"
               :class="{ ready: s.inArea && !isTestingStage(s.stage), testing: isTestingStage(s.stage) }">
            <div class="slot-head">
              <span class="slot-no">{{ s.slot }} 号位</span>
              <div class="slot-head-right">
                <span class="slot-light" :class="(s.inArea || isTestingStage(s.stage)) ? 'green' : 'gray'"></span>
                <van-icon v-if="!configLocked" name="cross" class="slot-delete-icon"
                          @click.stop="removeSlot(s)" />
              </div>
            </div>
            <div class="slot-name" :class="{ 'ready-color': s.inArea && !isTestingStage(s.stage) }">
              {{ s.studentName || '未命名' }}
            </div>
            <div class="slot-sub">{{ s.className || s.snCode || '' }}</div>

            <!-- 实时成绩展示（测试中） -->
            <template v-if="isTestingStage(s.stage)">
              <div class="slot-score">
                <div class="score-display">
                  <span class="score-num">{{ s.score || '0' }}</span>
                  <small class="score-unit">{{ currentProjectUnit }}</small>
                </div>
                <span class="score-time">剩 {{ s.remainingSeconds != null ? s.remainingSeconds : '--' }}s</span>
              </div>
              <div class="slot-progress">
                <div class="slot-progress-bar" :style="{ width: getRemainingPercent(s) + '%' }"></div>
              </div>
            </template>

            <!-- 准备就绪状态（非测试中） -->
            <div v-else class="slot-state-text" :class="{ 'ready-color': s.inArea }">
              {{ s.inArea ? '● 已就位' : '请考生进入测试圈' }}
            </div>
          </div>
        </div>
      </div>

      <!-- 操作按钮区 -->
      <div class="glass-container">
        <!-- 3-2-1 倒计时提示 -->
        <div v-if="phase === 'countdown'" class="countdown-card">
          <div class="countdown-circle">
            <span class="countdown-num">{{ countdownSeconds }}</span>
          </div>
          <div class="countdown-tip">准备倒计时中，请做好准备…</div>
        </div>

        <van-button v-if="phase === 'entered' || phase === 'assigned'" block round
                    :type="startButtonType"
                    :class="startButtonClass"
                    :disabled="slots.length === 0"
                    @click="onStart">
          {{ startButtonText }}
        </van-button>

        <template v-if="phase === 'countdown' || phase === 'testing'">
          <van-button block round type="warning" style="margin-bottom: 12px;" @click="onStop">
            中途停止（成绩入库）
          </van-button>
          <van-button block round type="danger" @click="onAbort">
            作废重测（成绩不入库）
          </van-button>
        </template>

        <van-button v-if="phase === 'entered' || phase === 'assigned'" block round plain type="default"
                    style="margin-top: 12px;" @click="onExit">
          退出受控模式
        </van-button>
      </div>
    </template>

    <!-- 未连接提示 -->
    <div v-else class="glass-container idle-tip">
      <van-icon name="info-o" size="18" color="#8e8e93" />
      <span>请先选择一台 AISport 大屏设备，连接后进入手机受控模式</span>
    </div>

    <!-- 设备选择弹窗 -->
    <van-popup v-model="showDevicePopup" position="bottom" round :style="{ height: '60%' }">
      <div class="popup-header">
        <span>选择设备</span>
        <van-icon name="cross" @click="showDevicePopup = false" />
      </div>
      <van-search v-model="searchText" placeholder="搜索设备名称 / IP" />
      <div class="device-list">
        <div v-for="item in filteredDeviceList" :key="item.code" class="device-item"
             :class="{ offline: !isOnline(item) }"
             @click="handleSelectDevice(item)">
          <span class="status-dot" :class="isOnline(item) ? 'on' : 'off'"></span>
          <div class="dev-main">
            <div class="name">{{ item.remark || '未知设备' }}</div>
            <div class="ip">
              {{ item.ip || 'No IP' }}
              <span class="last-time">最近在线 {{ formatLastTime(item.lastOnlineTime) }}</span>
            </div>
          </div>
          <span class="status-text" :class="isOnline(item) ? 'text-green' : 'text-gray'">
            {{ isOnline(item) ? '在线' : '离线' }}
          </span>
        </div>
        <van-empty v-if="filteredDeviceList.length === 0" description="暂无设备" />
      </div>
      <!-- 联调入口：手动输入设备真实 deviceId（32位MD5）直连 -->
      <div class="manual-device-entry" @click="openManualDevice">
        列表设备连不上？手动输入设备 ID（联调）
      </div>
    </van-popup>

    <!-- 手动输入 deviceId 弹窗 -->
    <van-dialog v-model="showManualDeviceDialog" title="手动输入设备 ID（联调）" show-cancel-button
                confirm-button-text="连接" @confirm="confirmManualDevice">
      <div class="manual-dialog-content">
        <div class="quick-fill-bar">
          <van-button size="small" type="info" plain round icon="desktop-o" @click="fillLabTestDevice">
            一键填入实验室测试机 (192.168.10.33)
          </van-button>
        </div>
        <van-field v-model="manualDeviceId" label="设备 ID" placeholder="32位设备ID，如 b661031a..."
                   :border="true" class="dialog-field" />
        <van-field v-model="manualDeviceIp" label="局域网 IP" placeholder="可选，用于 HTTP 直连"
                   :border="true" class="dialog-field" />
        <div class="switch-row">
          <span class="switch-label">启用局域网 HTTP 直连 (:8080)</span>
          <van-switch v-model="enableLocalHttp" size="20" />
        </div>
        <div v-if="manualDeviceIp" style="text-align: right; margin-bottom: 8px;">
          <van-button size="mini" type="info" plain round @click="testLocalHttpPing">
            测试 HTTP 连通性
          </van-button>
        </div>
        <div class="manual-hint">
          设备真实 ID 可在设备端查询（MD5，32位小写）。测试机：b661031a0c9ddb88507395067e06e64c
        </div>
      </div>
    </van-dialog>

    <!-- 自定义时长弹窗 -->
    <van-dialog v-model="showCustomDurationDialog" title="自定义测试时长" show-cancel-button
                confirm-button-text="确定" @confirm="confirmCustomDuration">
      <div style="padding: 16px 20px 8px;">
        <van-field v-model="customDurationInput" type="digit" label="时长(秒)"
                   placeholder="请输入 1 ~ 3600 秒" :border="true"
                   style="border: 1px solid #e5e5ea; border-radius: 8px;" />
        <div style="font-size: 12px; color: #8e8e93; margin-top: 8px; line-height: 1.5;">
          设置自定义时长（1 ~ 3600 秒），修改后若已选考生将实时同步大屏。
        </div>
      </div>
    </van-dialog>

    <!-- 项目选择弹窗 -->
    <van-popup v-model="showProjectPopup" position="bottom" round>
      <van-picker show-toolbar :columns="projectColumns" value-key="name"
                  @confirm="onProjectConfirm" @cancel="showProjectPopup = false" />
    </van-popup>

    <!-- 时长选择弹窗 -->
    <van-popup v-model="showDurationPopup" position="bottom" round>
      <van-picker show-toolbar :columns="durationColumns" value-key="text"
                  @confirm="onDurationConfirm" @cancel="showDurationPopup = false" />
    </van-popup>

    <!-- 选人弹窗 -->
    <van-popup v-model="showStudentPopup" position="bottom" round :style="{ height: '80%' }">
      <div class="popup-header">
        <span>选人上场（已选 {{ selectedStudents.length }}/{{ maxSlots }}）</span>
        <van-icon name="cross" @click="showStudentPopup = false" />
      </div>
      <div class="picker-body">
        <van-cell title="班级" :value="selectedClass ? selectedClass.text : '请选择'" is-link
                  @click="showClassPicker = true" />
        <van-search v-model="studentSearch" placeholder="搜索学生姓名" @search="loadStudents" />
        <div class="student-list">
          <div v-for="stu in studentList" :key="stu.snCode" class="student-item"
               :class="{ selected: isSelected(stu) }" @click="toggleStudent(stu)">
            <div class="stu-info">
              <div class="stu-name">{{ stu.studentName }}</div>
              <div class="stu-sn">{{ stu.snCode }}</div>
            </div>
            <div v-if="isSelected(stu)" class="stu-slot-badge">{{ slotOf(stu) }} 号位</div>
            <van-icon v-else name="plus" color="#c7c7cc" />
          </div>
          <van-empty v-if="studentList.length === 0" description="请选择班级加载学生" />
        </div>
        <div class="picker-footer">
          <van-button block round type="primary" :disabled="selectedStudents.length === 0"
                      :loading="assigning" @click="confirmAssign">
            确认分配（{{ selectedStudents.length }} 人）
          </van-button>
        </div>
      </div>
      <van-popup v-model="showClassPicker" position="bottom" round>
        <van-picker show-toolbar :columns="classList" value-key="text"
                    @confirm="onClassConfirm" @cancel="showClassPicker = false" />
      </van-popup>
    </van-popup>

    <!-- 成绩结算弹窗 -->
    <van-popup v-model="showResultPopup" position="bottom" round :close-on-click-overlay="false"
               class="result-popup">
      <div class="result-popup-inner">
        <div class="result-header">
          <div class="result-title">
            <van-icon name="checked" color="#34C759" size="22" />
            <span>本轮测试成绩结算</span>
          </div>
          <div class="result-sub">共 {{ results.length }} 位考生已完成测试并持久化入库</div>
        </div>

        <div class="result-list">
          <div v-for="r in results" :key="r.slot" class="result-item-card">
            <div class="r-top">
              <span class="r-slot-name">{{ r.slot }} 号位 · {{ r.studentName || '考生' }}</span>
              <div class="r-score-wrap">
                <span class="r-score">{{ r.score }}</span>
                <span class="r-unit">{{ currentProjectUnit }}</span>
              </div>
            </div>
            <div class="r-meta" v-if="r.className || r.snCode">
              <span v-if="r.className" class="r-class">{{ r.className }}</span>
              <span v-if="r.snCode" class="r-sn">学籍号: {{ r.snCode }}</span>
            </div>
            <div class="r-db-info" v-if="r.uniqueId">
              <span class="tag-stored">已入库</span>
              <span class="unique-id">流水号: {{ r.uniqueId }}</span>
            </div>
          </div>
          <van-empty v-if="results.length === 0" description="暂无结算成绩" />
        </div>

        <div class="result-actions">
          <van-button block round type="primary" class="btn-action" @click="onNextGroup">
            录入下一组
          </van-button>
          <van-button block round type="info" plain class="btn-action" @click="onRetryGroup">
            本组重测（原班人马）
          </van-button>
          <van-button block round type="default" plain class="btn-action btn-exit" @click="onExitFromResults">
            退出受控模式
          </van-button>
        </div>
      </div>
    </van-popup>
  </div>
</template>

<script>
import { Dialog, Toast } from 'vant';
import moment from 'moment';
import { selectSchoolDeviceList, selectSchoolDeviceStatus } from '@/api/teacher/deviceControl.js';
import { getClassInfoList, getStudentInfoList } from '@/api/teacherAttendanceCall.js';
import {
  AISportPhoneController,
  PROJECT_OPTIONS,
  SLOT_STAGE,
  LAB_TEST_DEVICE,
  sendLocalHttpControl,
} from '@/utils/aisportPhoneControl.js';

export default {
  name: 'DevicePhoneControl',
  data() {
    return {
      teacherTitleInfo: {},
      // 连接
      mqttConnected: false,
      connecting: false,
      // 设备
      deviceList: [],
      deviceData: {},
      showDevicePopup: false,
      searchText: '',
      // 手动输入 deviceId（联调）
      showManualDeviceDialog: false,
      manualDeviceId: '',
      manualDeviceIp: '',
      enableLocalHttp: false,
      // 自定义时长
      showCustomDurationDialog: false,
      customDurationInput: '',
      // 业务阶段: idle(未连接) -> entered(受控) -> assigned(已选人) -> countdown -> testing
      phase: 'idle',
      countdownSeconds: 3,
      // 配置
      projectCode: 'RopeSkipping',
      duration: 0,
      showProjectPopup: false,
      showDurationPopup: false,
      durationColumns: [
        { text: '跟随设备默认', value: 0 },
        { text: '30 秒', value: 30 },
        { text: '60 秒', value: 60 },
        { text: '90 秒', value: 90 },
        { text: '120 秒', value: 120 },
        { text: '自定义时长…', value: 'custom' },
      ],
      // 工位
      slots: [],
      lastTestedSlots: [],
      lastTestedStudents: [],
      // 选人
      showStudentPopup: false,
      showClassPicker: false,
      classList: [],
      selectedClass: null,
      studentList: [],
      studentSearch: '',
      selectedStudents: [],
      assigning: false,
      // 结算
      results: [],
      showResultPopup: false,
    };
  },
  computed: {
    projectColumns() {
      return PROJECT_OPTIONS;
    },
    currentProjectName() {
      const p = PROJECT_OPTIONS.find((x) => (x.projectCode || x.code) === this.projectCode);
      return p ? (p.projectName || p.name) : this.projectCode;
    },
    currentProjectUnit() {
      const p = PROJECT_OPTIONS.find((x) => (x.projectCode || x.code) === this.projectCode);
      return (p && (p.projectUnit || p.unit)) || '次';
    },
    maxSlots() {
      const p = PROJECT_OPTIONS.find((x) => (x.projectCode || x.code) === this.projectCode);
      if (p && p.singleSlot) return 1;
      if (p && p.maxSlots) return p.maxSlots;
      return 8;
    },
    durationText() {
      if (this.duration > 0) return `${this.duration} 秒`;
      return '跟随设备默认';
    },
    configLocked() {
      return this.phase === 'countdown' || this.phase === 'testing';
    },
    readySlotCount() {
      return this.slots.filter((s) => !!s.inArea).length;
    },
    startButtonType() {
      if (this.slots.length > 0 && this.readySlotCount === 0) {
        return 'warning';
      }
      return 'primary';
    },
    startButtonClass() {
      if (this.slots.length > 0 && this.readySlotCount > 0) {
        return 'btn-start-ready';
      }
      if (this.slots.length > 0 && this.readySlotCount === 0) {
        return 'btn-start-empty';
      }
      return '';
    },
    startButtonText() {
      if (this.slots.length === 0) return '请先选人分配工位';
      if (this.readySlotCount > 0) {
        return `开始测试（已就位 ${this.readySlotCount}/${this.slots.length} 人）`;
      }
      return `开始测试（当前无人进圈 0/${this.slots.length}）`;
    },
    connStatusText() {
      if (this.mqttConnected) return '已连接 · 受控中';
      if (this.connecting) return '连接中…';
      if (this.deviceData.code) return '连接断开';
      return '未选择设备';
    },
    filteredDeviceList() {
      let list = this.deviceList;
      if (this.searchText) {
        const key = this.searchText.toLowerCase();
        list = list.filter((item) => ((item.remark || '') + (item.ip || '')).toLowerCase().includes(key));
      }
      // 与 /deviceControl 一致：在线设备排在前列
      return list.slice().sort((a, b) => {
        const aOn = this.isOnline(a) ? 1 : 0;
        const bOn = this.isOnline(b) ? 1 : 0;
        return bOn - aOn;
      });
    },
  },
  mounted() {
    this.teacherTitleInfo = JSON.parse(sessionStorage.getItem('teacherTitleInfo') || '{}');
    this.loadDevices();

    // 修复切后台/锁屏误触发 exit：切不可在 document.hidden 时 releaseDevice()！
    // 仅在页面重新可见时检查重连，组件销毁或用户主动退出时才 releaseDevice()
    this._onVisibilityChange = () => {
      if (!document.hidden) {
        if (this.deviceData && this.deviceIdOf(this.deviceData) && !this.mqttConnected) {
          Toast('页面已恢复，正在检查设备连接…');
          if (!this.controller || !this.controller.connected) {
            this.connectDevice();
          }
        }
      }
    };
    document.addEventListener('visibilitychange', this._onVisibilityChange);
  },
  beforeDestroy() {
    if (this._onVisibilityChange) {
      document.removeEventListener('visibilitychange', this._onVisibilityChange);
    }
    this.clearCountdownTimer();
    this.releaseDevice();
  },
  methods: {
    // 与 /deviceControl 一致的在线判定：onlineStatus 优先，lastOnlineTime 时间窗兜底
    isOnline(device) {
      if (!device) return false;
      const status = device.onlineStatus;
      if (status !== undefined && status !== null && status !== '') {
        return status === '1' || status === 1;
      }
      const time = device.lastOnlineTime;
      if (time && moment(time).isValid()) {
        const diffMinutes = moment().diff(moment(time), 'minutes');
        return diffMinutes >= -5 && diffMinutes < 10;
      }
      return false;
    },
    formatLastTime(time) {
      if (!time || !moment(time).isValid()) return '--';
      return moment(time).isSame(moment(), 'day')
        ? moment(time).format('HH:mm')
        : moment(time).format('MM-DD');
    },
    loadDevices() {
      selectSchoolDeviceList({
        userId: this.teacherTitleInfo.teacherId,
        userName: this.teacherTitleInfo.teacherName,
        schoolCode: this.teacherTitleInfo.schoolCode,
        projectCode: 'tiaotiao',
      }).then((res) => {
        if (res.data && res.data.body) {
          this.deviceList = res.data.body;
          this.refreshOnlineStatus();
        }
      }).catch(() => {});
    },
    // 列表接口可能不带在线状态字段，逐台查询 selectSchoolDeviceStatus 补齐（与 /deviceControl 选中设备时的数据源一致）
    refreshOnlineStatus() {
      this.deviceList.forEach((item, idx) => {
        const hasStatus = (item.onlineStatus !== undefined && item.onlineStatus !== null && item.onlineStatus !== '')
          || item.lastOnlineTime;
        if (hasStatus) return;
        selectSchoolDeviceStatus({ deviceId: item.code, schoolCode: item.schoolCode })
          .then((res) => {
            const body = res.data && res.data.body;
            if (body) {
              this.$set(this.deviceList, idx, { ...item, ...body });
            }
          })
          .catch(() => {});
      });
    },
    openDevicePopup() {
      this.showDevicePopup = true;
      this.refreshOnlineStatus();
    },
    openManualDevice() {
      this.manualDeviceId = '';
      this.manualDeviceIp = '';
      this.enableLocalHttp = false;
      this.showManualDeviceDialog = true;
    },
    fillLabTestDevice() {
      this.manualDeviceId = LAB_TEST_DEVICE.deviceId;
      this.manualDeviceIp = LAB_TEST_DEVICE.ip;
      this.enableLocalHttp = true;
      Toast.success('已填入实验室测试机信息');
    },
    testLocalHttpPing() {
      const ip = (this.manualDeviceIp || '').trim();
      if (!ip) {
        Toast.fail('请先输入设备局域网 IP');
        return;
      }
      Toast.loading({ message: '正在测试局域网 HTTP 连通性…', forbidClick: true, duration: 2000 });
      sendLocalHttpControl(ip, 'enter', { projectCode: this.projectCode })
        .then((res) => {
          Toast.success('HTTP 直连成功 (200 OK)');
          console.log('[AISport] testLocalHttpPing res:', res);
        })
        .catch((err) => {
          Toast.fail(`HTTP 直连失败: ${err.message || '网络不可达'}`);
        });
    },
    confirmManualDevice() {
      const id = (this.manualDeviceId || '').trim();
      if (!/^[0-9a-fA-F]{32}$/.test(id)) {
        Toast.fail('设备 ID 应为 32 位十六进制字符串');
        return;
      }
      this.showDevicePopup = false;
      this.handleSelectDevice({
        code: id.toLowerCase(),
        deviceId: id.toLowerCase(),
        remark: `手动设备 ${id.substring(0, 6)}…`,
        ip: (this.manualDeviceIp || '').trim(),
        enableLocalHttp: this.enableLocalHttp,
        schoolCode: this.teacherTitleInfo.schoolCode,
      });
    },
    handleSelectDevice(item) {
      this.showDevicePopup = false;
      if (this.deviceData.code === item.code && this.mqttConnected) return;
      this.releaseDevice();
      this.deviceData = item;
      this.phase = 'idle';
      this.slots = [];
      this.connectDevice();
    },
    deviceIdOf(item) {
      if (!item) return '';
      return item.mqttDeviceId || item.deviceId || item.code || '';
    },
    connectDevice() {
      const deviceId = this.deviceIdOf(this.deviceData);
      if (!deviceId) {
        Toast.fail('该设备缺少 deviceId，无法连接');
        return;
      }
      this.connecting = true;
      // 连接看门狗：12 秒内未成功则按超时处理
      if (this._connWatchdog) clearTimeout(this._connWatchdog);
      this._connWatchdog = setTimeout(() => {
        if (this.connecting && !this.mqttConnected) {
          this.connecting = false;
          Toast.fail('连接超时：请检查手机网络，或 Broker 的 wss 端口/证书是否正常');
        }
      }, 12000);

      const handlers = {
        onReady: () => {
          if (this._connWatchdog) clearTimeout(this._connWatchdog);
          this.connecting = false;
          this.mqttConnected = true;
          Toast.success('设备连接成功');
          // 连接成功后进入手机受控模式：先切到操作界面，再等设备 ack
          this.phase = 'entered';
          try {
            this.controller.enter(this.projectCode);
          } catch (e) {
            Toast.fail(e.message);
            return;
          }
          // 应答看门狗：5 秒内设备未回 enter ack
          if (this._enterAckTimer) clearTimeout(this._enterAckTimer);
          this._enterAckTimer = setTimeout(() => {
            if (!this._enterAcked && this.mqttConnected) {
              Toast.fail('设备未应答：请确认设备在线，且设备标识（deviceId）与 MQTT Topic 匹配');
            }
          }, 5000);
          this._enterAcked = false;
        },
        onError: (err) => {
          const wasConnecting = this.connecting;
          if (this._connWatchdog) clearTimeout(this._connWatchdog);
          this.connecting = false;
          this.mqttConnected = false;
          console.error('[AISport] MQTT 错误:', err);
          if (wasConnecting) Toast.fail(this.classifyConnError(err));
        },
        onClose: () => {
          const wasConnected = this.mqttConnected;
          this.mqttConnected = false;
          if (wasConnected) Toast('与设备的连接已断开');
        },
        onAck: this.handleAck,
        onSlotStatus: this.handleSlotStatus,
        onLiveScore: this.handleLiveScore,
        onTestFinished: this.handleTestFinished,
      };

      const options = {
        deviceIp: this.deviceData.ip || '',
        useHttp: !!this.deviceData.enableLocalHttp,
      };

      this.controller = new AISportPhoneController(deviceId, handlers, options);
      this.controller.connect();
    },
    // 连接错误分类，便于现场联调定位
    classifyConnError(err) {
      const msg = ((err && (err.message || err.toString())) || '').toLowerCase();
      if (/not authorized|bad username|bad password|authentication|unauthorized/.test(msg)) {
        return '认证失败：MQTT 用户名或密码错误';
      }
      if (/cert|ssl|tls|self.signed|handshake/.test(msg)) {
        return '安全证书校验失败：Broker 的 wss 证书不受信任，需更换正规 CA 证书';
      }
      if (/timeout|timed out/.test(msg)) {
        return '连接超时：请检查网络或 Broker 的 wss 端口是否开放';
      }
      if (/refused|unreachable|failed to fetch|network|websocket|socket/.test(msg)) {
        return '网络不可达：无法连接 Broker（wss://tiaotiao.xtotoro.com:38084）';
      }
      return `设备连接异常：${(err && err.message) || '未知错误'}`;
    },
    releaseDevice() {
      if (this._connWatchdog) { clearTimeout(this._connWatchdog); this._connWatchdog = null; }
      if (this._enterAckTimer) { clearTimeout(this._enterAckTimer); this._enterAckTimer = null; }
      if (this._assignAckTimer) { clearTimeout(this._assignAckTimer); this._assignAckTimer = null; }
      this.clearCountdownTimer();
      this._enterAcked = false;
      this.assigning = false;
      if (this.controller) {
        // 页面销毁/换设备时主动退出受控模式，释放大屏
        if (this.controller.connected && this.phase !== 'idle') {
          try { this.controller.exit(); } catch (e) { /* ignore */ }
        }
        this.controller.disconnect();
        this.controller = null;
      }
      this.mqttConnected = false;
      this.connecting = false;
    },

    // ================= 设备上行消息 =================
    handleAck(msg) {
      if (msg.status === 'error') {
        if (msg.reason === 'no_player_in_area') {
          Toast('当前测试区域内未检测到考生，请让考生进入对应框线内');
        } else if (msg.reason === 'inactivity_timeout') {
          Toast('长时间无操作，设备已自动退出控制模式');
        } else {
          Toast.fail(`操作失败：${msg.reason || msg.message || '未知原因'}`);
        }
      }
      // 10 分钟静默超时，设备端自动退出
      if (msg.subAction === 'timeout_exit') {
        Toast('由于长时间无操作，已自动退出控制模式');
        this.resetToIdle();
        return;
      }
      if (msg.status !== 'ok') return;
      switch (msg.subAction) {
        case 'enter':
          this._enterAcked = true;
          if (this._enterAckTimer) clearTimeout(this._enterAckTimer);
          this.phase = this.slots.length ? 'assigned' : 'entered';
          break;
        case 'assign':
          this.assigning = false;
          if (this._assignAckTimer) clearTimeout(this._assignAckTimer);
          this.showStudentPopup = false;
          this.phase = 'assigned';
          if (Array.isArray(msg.slots)) this.mergeSlots(msg.slots);
          Toast.success('选人成功，等待考生进圈');
          break;
        case 'start':
          // 部分到场时设备自动剔除未就绪工位，以 ack 返回的 slots 为准
          if (Array.isArray(msg.slots) && msg.slots.length) {
            this.mergeSlots(msg.slots, true);
          }
          this.phase = 'countdown';
          this.startCountdown();
          break;
        case 'stop':
          this.clearCountdownTimer();
          break; // 等 phone_test_finished 结算广播
        case 'abort':
          this.clearCountdownTimer();
          this.phase = 'assigned';
          // 完整重置每项工位状态并触发响应式更新
          this.slots = this.slots.map((s) => ({
            ...s,
            stage: SLOT_STAGE.READY,
            score: '0',
            remainingSeconds: null,
          }));
          Toast('成绩已作废，考生重新进圈后可再次开测');
          break;
        case 'exit':
          this.resetToIdle();
          Toast('已退出受控模式');
          break;
        default:
          break;
      }
    },
    handleSlotStatus(msg) {
      if (Array.isArray(msg.slots)) this.mergeSlots(msg.slots);
    },
    handleLiveScore(msg) {
      if (!Array.isArray(msg.slots)) return;
      this.mergeSlots(msg.slots);
      // 依据工位阶段推进整体状态
      const stages = msg.slots.map((s) => s.stage);
      if (stages.includes(SLOT_STAGE.TESTING)) {
        this.clearCountdownTimer();
        this.phase = 'testing';
      } else if (stages.includes(SLOT_STAGE.COUNTDOWN)) {
        if (this.phase !== 'countdown') {
          this.phase = 'countdown';
          this.startCountdown();
        }
      }
    },
    handleTestFinished(msg) {
      this.clearCountdownTimer();
      const rawResults = Array.isArray(msg.results) ? msg.results : [];
      this.results = rawResults.map((r) => {
        const local = this.slots.find((s) => s.slot === r.slot || s.snCode === r.snCode) || {};
        return {
          ...local,
          ...r,
        };
      });
      // 保存上一组考生快照用于“本组重测”
      this.lastTestedSlots = this.slots.map((s) => ({ ...s }));
      this.lastTestedStudents = this.selectedStudents.slice();
      this.showResultPopup = true;
      this.phase = 'entered';
      this.slots = []; // 设备端已自动清空本轮工位
    },
    /**
     * 重构 mergeSlots：
     * 当 replace === true 时，以 serverSlots 为准过滤本地工位，保留本地班级元数据，
     * 且 inArea 默认保持 true，若过滤掉空位则轻提示。
     */
    mergeSlots(serverSlots, replace = false) {
      if (!Array.isArray(serverSlots)) return;
      if (replace) {
        const prevCount = this.slots.length;
        const newSlots = [];
        serverSlots.forEach((ss) => {
          const localSlot = this.slots.find((x) => x.slot === ss.slot) || {};
          const merged = {
            ...localSlot,
            ...ss,
            inArea: ss.inArea !== undefined ? ss.inArea : (localSlot.inArea !== undefined ? localSlot.inArea : true),
            score: ss.score !== undefined ? ss.score : (localSlot.score || '0'),
          };
          newSlots.push(merged);
        });
        newSlots.sort((a, b) => a.slot - b.slot);
        this.slots = newSlots;
        // 同步更新 selectedStudents
        this.selectedStudents = this.selectedStudents.filter((stu) =>
          this.slots.some((s) => s.snCode === stu.snCode)
        );
        if (this.slots.length < prevCount) {
          Toast(`已自动过滤 ${prevCount - this.slots.length} 个未进圈空位，共 ${this.slots.length} 人开测`);
        }
        return;
      }
      serverSlots.forEach((ss) => {
        const idx = this.slots.findIndex((x) => x.slot === ss.slot);
        if (idx >= 0) {
          this.$set(this.slots, idx, { ...this.slots[idx], ...ss });
        } else {
          this.slots.push({ inArea: false, score: '0', ...ss });
        }
      });
      this.slots.sort((a, b) => a.slot - b.slot);
    },

    // ================= 倒计时控制 =================
    startCountdown() {
      this.clearCountdownTimer();
      this.countdownSeconds = 3;
      this._countdownTimer = setInterval(() => {
        if (this.countdownSeconds > 1) {
          this.countdownSeconds -= 1;
        } else {
          this.clearCountdownTimer();
        }
      }, 1000);
    },
    clearCountdownTimer() {
      if (this._countdownTimer) {
        clearInterval(this._countdownTimer);
        this._countdownTimer = null;
      }
    },
    getRemainingPercent(s) {
      if (s.remainingSeconds == null) return 100;
      let total = this.duration;
      if (!total || total <= 0) {
        const p = PROJECT_OPTIONS.find((x) => x.code === this.projectCode);
        total = (p && p.defaultDuration) || 60;
      }
      if (total <= 0) return 100;
      const pct = Math.max(0, Math.min(100, (s.remainingSeconds / total) * 100));
      return Math.round(pct);
    },

    // ================= 下行指令 =================
    ensureConnected() {
      if (!this.controller || !this.controller.connected) {
        Toast.fail('设备未连接');
        return false;
      }
      return true;
    },
    onStart() {
      if (!this.ensureConnected()) return;
      if (this.slots.length === 0) {
        Toast('请先选择考生并分配工位');
        return;
      }
      if (this.readySlotCount === 0) {
        Toast('提示：视觉尚未检测到就位考生，正在向大屏请求开测…');
      }
      try {
        this.controller.start();
      } catch (e) { Toast.fail(e.message); }
    },
    onStop() {
      Dialog.confirm({ title: '中途停止', message: '停止后本轮成绩将正常入库结算，确认停止？' })
        .then(() => {
          if (!this.ensureConnected()) return;
          try { this.controller.stop(); } catch (e) { Toast.fail(e.message); }
        })
        .catch(() => {});
    },
    onAbort() {
      Dialog.confirm({ title: '作废重测', message: '本轮成绩将彻底作废（不入库），工位选人保留，确认作废？' })
        .then(() => {
          if (!this.ensureConnected()) return;
          try { this.controller.abort(); } catch (e) { Toast.fail(e.message); }
        })
        .catch(() => {});
    },
    onExit() {
      Dialog.confirm({ title: '退出受控模式', message: '退出后大屏恢复普通模式，确认退出？' })
        .then(() => {
          if (this.controller && this.controller.connected) {
            try { this.controller.exit(); } catch (e) { /* ignore */ }
          }
          this.resetToIdle();
        })
        .catch(() => {});
    },
    resetToIdle() {
      this.clearCountdownTimer();
      this.phase = 'idle';
      this.slots = [];
      this.selectedStudents = [];
      this.results = [];
      this.showResultPopup = false;
      this.releaseDevice();
      this.deviceData = {};
    },

    // 成绩结算后操作
    onNextGroup() {
      this.showResultPopup = false;
      this.results = [];
      this.slots = [];
      this.selectedStudents = [];
      this.phase = 'entered'; // 仍处于受控模式，自动开启选人
      this.openStudentPicker();
    },
    onRetryGroup() {
      if (!this.ensureConnected()) return;
      if (!this.lastTestedSlots || this.lastTestedSlots.length === 0) {
        Toast('暂无上一组考生信息');
        return;
      }
      this.showResultPopup = false;
      this.results = [];
      this.slots = this.lastTestedSlots.map((s) => ({
        ...s,
        inArea: false,
        stage: SLOT_STAGE.READY,
        score: '0',
        remainingSeconds: null,
      }));
      this.selectedStudents = this.lastTestedStudents.slice();
      this.phase = 'assigned';
      try {
        this.controller.assign(this.slots, this.duration, this.projectCode);
        Toast.success('已恢复本组考生，等待进圈重新开测');
      } catch (e) {
        Toast.fail(e.message);
      }
    },
    onExitFromResults() {
      this.showResultPopup = false;
      this.onExit();
    },

    // ================= 选人与工位调整 =================
    openStudentPicker() {
      this.showStudentPopup = true;
      if (this.classList.length === 0) this.loadClasses();
    },
    loadClasses() {
      getClassInfoList({}).then((res) => {
        const data = (res.data && (res.data.data || res.data.body)) || [];
        this.classList = data.map((item) => ({
          text: `${item.gradeName || ''}-${item.className || ''}`,
          value: item.classCode,
          raw: item,
        }));
      }).catch(() => {});
    },
    onClassConfirm(val) {
      this.selectedClass = val;
      this.showClassPicker = false;
      this.loadStudents();
    },
    loadStudents() {
      if (!this.selectedClass) return;
      getStudentInfoList({
        classCode: this.selectedClass.value,
        studentName: this.studentSearch || '',
      }).then((res) => {
        this.studentList = (res.data && (res.data.data || res.data.body)) || [];
      }).catch(() => {});
    },
    isSelected(stu) {
      return this.selectedStudents.some((x) => x.snCode === stu.snCode);
    },
    slotOf(stu) {
      const idx = this.selectedStudents.findIndex((x) => x.snCode === stu.snCode);
      return idx >= 0 ? idx + 1 : 0;
    },
    toggleStudent(stu) {
      const idx = this.selectedStudents.findIndex((x) => x.snCode === stu.snCode);
      if (idx >= 0) {
        this.selectedStudents.splice(idx, 1);
        return;
      }
      if (this.selectedStudents.length >= this.maxSlots) {
        Toast(`本项目最多支持 ${this.maxSlots} 个工位`);
        return;
      }
      this.selectedStudents.push(stu);
    },
    removeSlot(slotItem) {
      if (this.configLocked) return;
      const idx = this.slots.findIndex((s) => s.slot === slotItem.slot);
      if (idx < 0) return;
      const removed = this.slots.splice(idx, 1)[0];
      this.selectedStudents = this.selectedStudents.filter((x) => x.snCode !== removed.snCode);
      this.slots.forEach((s, i) => {
        s.slot = i + 1;
      });
      if (this.slots.length === 0) {
        this.phase = 'entered';
        Toast('已清空所有工位');
        return;
      }
      Toast(`已移除 ${removed.studentName || (removed.slot + ' 号位')}`);
      if (this.controller && this.controller.connected) {
        try {
          this.controller.assign(this.slots, this.duration, this.projectCode);
        } catch (e) {
          console.warn('[AISport] 移除工位重新同步失败:', e);
        }
      }
    },
    confirmAssign() {
      if (!this.ensureConnected()) return;
      if (this.selectedStudents.length === 0) return;
      const t = this.teacherTitleInfo;
      const players = this.selectedStudents.map((stu, i) => ({
        slot: i + 1,
        snCode: stu.snCode || '',
        studentName: stu.studentName || '',
        className: stu.className || (this.selectedClass && this.selectedClass.raw.className) || '',
        classId: stu.classCode || (this.selectedClass && this.selectedClass.value) || '',
        gradeName: stu.gradeName || (this.selectedClass && this.selectedClass.raw.gradeName) || '',
        studentGrade: stu.gradeName || '',
        schoolCode: stu.schoolCode || t.schoolCode || '',
        schoolName: stu.schoolName || t.schoolName || '',
      }));
      this.assigning = true;
      try {
        this.controller.assign(players, this.duration, this.projectCode);
        // 先本地渲染工位（等 ack/状态推送更新 inArea）
        this.slots = players.map((p) => ({
          ...p,
          inArea: false,
          stage: SLOT_STAGE.READY,
          score: '0',
          remainingSeconds: null,
        }));
        this.phase = 'assigned';
        // 选人应答看门狗：5 秒内设备未回 assign ack，解除转圈并提示（工位本地保留）
        if (this._assignAckTimer) clearTimeout(this._assignAckTimer);
        this._assignAckTimer = setTimeout(() => {
          if (this.assigning) {
            this.assigning = false;
            this.showStudentPopup = false;
            Toast.fail('设备未应答选人指令：工位已本地展示，请确认设备在线（或手动输入真实设备 ID）后重试');
          }
        }, 5000);
      } catch (e) {
        this.assigning = false;
        Toast.fail(e.message);
      }
    },

    // ================= 配置调整与同步 =================
    onProjectConfirm(val) {
      this.projectCode = val.projectCode || val.code;
      this.showProjectPopup = false;

      // 检查工位数限制（如从多人工位切到单人工位项目）
      if (this.slots.length > this.maxSlots) {
        this.slots = this.slots.slice(0, this.maxSlots);
        this.selectedStudents = this.selectedStudents.slice(0, this.maxSlots);
        Toast(`本项目最多支持 ${this.maxSlots} 个工位，已自动保留前 ${this.maxSlots} 位考生`);
      }

      // 切换项目后通知大屏
      if (this.controller && this.controller.connected && this.phase !== 'idle') {
        try {
          this.controller.enter(this.projectCode);
          // 若当前已有分配工位，延迟 200ms 自动重新下发 assign，确保大屏切换后保留考生
          if (this.slots.length > 0) {
            setTimeout(() => {
              if (this.controller && this.controller.connected && this.slots.length > 0) {
                try {
                  this.controller.assign(this.slots, this.duration, this.projectCode);
                } catch (e) {
                  console.warn('[AISport] 切换项目重新同步工位失败:', e);
                }
              }
            }, 200);
          }
        } catch (e) {
          Toast.fail(e.message);
        }
      }
    },
    onDurationConfirm(val) {
      if (val.value === 'custom') {
        this.showDurationPopup = false;
        this.customDurationInput = this.duration > 0 ? String(this.duration) : '';
        this.showCustomDurationDialog = true;
        return;
      }
      this.duration = val.value;
      this.showDurationPopup = false;
      this.syncDurationToDevice();
    },
    confirmCustomDuration() {
      const sec = parseInt(this.customDurationInput, 10);
      if (isNaN(sec) || sec < 1 || sec > 3600) {
        Toast.fail('自定义时长需在 1 ~ 3600 秒之间');
        return;
      }
      this.duration = sec;
      this.showCustomDurationDialog = false;
      this.syncDurationToDevice();
    },
    syncDurationToDevice() {
      const desc = this.duration > 0 ? `${this.duration} 秒` : '跟随设备默认';
      Toast(`已设置测试时长: ${desc}`);
      // 修改时长后，若已处于 assigned 阶段且工位有人，自动重新下发 assign 同步给大屏
      if (this.controller && this.controller.connected && this.slots.length > 0 && (this.phase === 'assigned' || this.phase === 'entered')) {
        try {
          this.controller.assign(this.slots, this.duration, this.projectCode);
        } catch (e) {
          console.warn('[AISport] 同步时长至大屏失败:', e);
        }
      }
    },
    isTestingStage(stage) {
      return stage === SLOT_STAGE.COUNTDOWN || stage === SLOT_STAGE.TESTING;
    },
  },
};
</script>

<style lang="less" scoped>
.ios-glass-page {
  min-height: 100vh;
  width: 100%;
  background: radial-gradient(circle at top left, #f0f2f5, #cfd9df);
  font-family: -apple-system, BlinkMacSystemFont, "PingFang SC", Helvetica, Arial, sans-serif;
  padding: 16px;
  box-sizing: border-box;
  overflow-y: auto;
  position: relative;
}
.background-blobs {
  position: fixed; top: 0; left: 0; width: 100%; height: 100%;
  z-index: 0; pointer-events: none;
  .blob { position: absolute; border-radius: 50%; filter: blur(60px); opacity: 0.5; }
  .blob-1 { width: 260px; height: 260px; background: #d0e1ff; top: -60px; left: -40px; }
  .blob-2 { width: 220px; height: 220px; background: #ffe5d0; bottom: 20%; right: -30px; }
  .blob-3 { width: 180px; height: 180px; background: #e0f2f1; top: 40%; left: 40%; opacity: 0.4; }
}
.glass-container {
  position: relative; z-index: 1;
  background: rgba(255, 255, 255, 0.25);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  border: 1px solid rgba(255, 255, 255, 0.8);
  box-shadow: 0 4px 24px -1px rgba(0, 0, 0, 0.05);
  border-radius: 20px;
  padding: 16px;
  margin-bottom: 16px;
}
.panel-title {
  font-size: 13px; font-weight: 600; color: #6e6e73;
  margin: 0 0 10px 8px; letter-spacing: 0.5px;
  display: flex; justify-content: space-between; align-items: center;
  .panel-action { color: #007AFF; font-weight: 500; cursor: pointer; }
}

/* 设备卡片 */
.device-card {
  display: flex; align-items: center; cursor: pointer;
  .device-icon {
    width: 44px; height: 44px; border-radius: 12px; margin-right: 12px;
    background: #c7c7cc;
    display: flex; align-items: center; justify-content: center;
    &.online { background: #34C759; box-shadow: 0 4px 10px rgba(52, 199, 89, 0.3); }
  }
  .device-content {
    flex: 1;
    .device-name { font-size: 16px; font-weight: 600; color: #1c1c1e; }
    .device-status {
      font-size: 12px; color: #8e8e93; margin-top: 4px;
      .text-green { color: #34C759; }
      .text-gray { color: #8e8e93; }
      .divider { margin: 0 6px; color: #d1d1d6; }
      .http-tag {
        margin-left: 6px;
        background: #007AFF;
        color: #fff;
        padding: 1px 5px;
        border-radius: 4px;
        font-size: 10px;
        font-weight: 600;
      }
    }
  }
  .arrow-btn { color: #c7c7cc; }
}

/* 工位卡片 */
.empty-slots {
  display: flex; align-items: center; justify-content: center; gap: 8px;
  padding: 28px 0; color: #8e8e93; font-size: 14px;
  border: 1px dashed #d1d1d6; border-radius: 14px; background: rgba(255,255,255,0.5);
  cursor: pointer;
}
.slot-grid {
  display: grid; grid-template-columns: 1fr 1fr; gap: 12px;
}
.slot-card {
  background: #ffffffc4; border-radius: 16px; padding: 12px;
  border: 2px solid #e5e5ea;
  transition: all 0.25s ease;
  position: relative;
  &.ready { border-color: #34C759; box-shadow: 0 4px 12px rgba(52, 199, 89, 0.15); }
  &.testing { border-color: #007AFF; box-shadow: 0 4px 12px rgba(0, 122, 255, 0.15); }
  .slot-head {
    display: flex; justify-content: space-between; align-items: center;
    .slot-no { font-size: 12px; color: #8e8e93; font-weight: 600; }
    .slot-head-right {
      display: flex; align-items: center; gap: 8px;
      .slot-light {
        width: 10px; height: 10px; border-radius: 50%;
        &.gray { background: #d1d1d6; }
        &.green { background: #34C759; box-shadow: 0 0 6px rgba(52,199,89,0.7); }
      }
      .slot-delete-icon {
        color: #c7c7cc; font-size: 14px; cursor: pointer; padding: 2px;
        &:active { color: #ff3b30; }
      }
    }
  }
  .slot-name {
    font-size: 16px; font-weight: 600; color: #1c1c1e; margin-top: 6px;
    &.ready-color { color: #34C759; }
  }
  .slot-sub { font-size: 11px; color: #8e8e93; margin-top: 2px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .slot-state-text {
    font-size: 12px; color: #8e8e93; margin-top: 8px;
    &.ready-color { color: #34C759; font-weight: 600; }
  }
  .slot-score {
    margin-top: 6px; display: flex; align-items: baseline; justify-content: space-between;
    .score-display {
      display: flex; align-items: baseline; gap: 2px;
      .score-num { font-size: 26px; font-weight: 700; color: #007AFF; }
      .score-unit { font-size: 12px; color: #8e8e93; font-weight: 500; }
    }
    .score-time { font-size: 12px; color: #FF9500; font-weight: 600; }
  }
  .slot-progress {
    margin-top: 6px; height: 4px; background: #e5e5ea; border-radius: 2px; overflow: hidden;
    .slot-progress-bar {
      height: 100%; background: linear-gradient(90deg, #34C759, #007AFF);
      border-radius: 2px; transition: width 0.3s ease-out;
    }
  }
}

/* 准备倒计时 */
.countdown-card {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: 12px 0 18px;
  .countdown-circle {
    width: 60px; height: 60px; border-radius: 50%;
    background: linear-gradient(135deg, #FF9500, #FF5E3A);
    box-shadow: 0 4px 16px rgba(255, 149, 0, 0.4);
    display: flex; align-items: center; justify-content: center;
    animation: pulseBadge 1s infinite alternate;
  }
  .countdown-num {
    font-size: 32px; font-weight: 800; color: #ffffff; line-height: 1;
  }
  .countdown-tip {
    font-size: 14px; font-weight: 600; color: #FF9500; margin-top: 10px;
    letter-spacing: 0.5px;
  }
}
@keyframes pulseBadge {
  0% { transform: scale(0.92); }
  100% { transform: scale(1.08); }
}

.idle-tip {
  display: flex; align-items: center; gap: 8px;
  color: #8e8e93; font-size: 14px; padding: 20px 16px;
}

/* 按钮样式优化 */
.btn-start-ready {
  background: linear-gradient(135deg, #34C759, #28a745) !important;
  border: none !important;
  box-shadow: 0 4px 14px rgba(52, 199, 89, 0.35);
}
.btn-start-empty {
  background: #ff9500 !important;
  border-color: #ff9500 !important;
  box-shadow: 0 4px 14px rgba(255, 149, 0, 0.25);
}

/* 弹窗通用 */
.popup-header {
  display: flex; justify-content: space-between; align-items: center;
  padding: 16px; font-size: 16px; font-weight: 600;
}
.device-list { padding: 0 16px 16px; overflow-y: auto; max-height: calc(60vh - 120px); }
.manual-device-entry {
  text-align: center; font-size: 13px; color: #007AFF;
  padding: 12px 16px calc(16px + env(safe-area-inset-bottom));
  cursor: pointer;
}
.device-item {
  background: #fff; border-radius: 12px; padding: 14px; margin-bottom: 10px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  display: flex; align-items: center; gap: 10px;
  &.offline { opacity: 0.55; }
  .status-dot {
    width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0;
    &.on { background: #34C759; box-shadow: 0 0 6px rgba(52,199,89,0.6); }
    &.off { background: #d1d1d6; }
  }
  .dev-main { flex: 1; min-width: 0; }
  .name { font-size: 15px; font-weight: 500; }
  .ip { font-size: 12px; color: #8e8e93; margin-top: 4px; }
  .last-time { margin-left: 8px; color: #c7c7cc; }
  .status-text {
    font-size: 12px; flex-shrink: 0;
    &.text-green { color: #34C759; }
    &.text-gray { color: #8e8e93; }
  }
}

.manual-dialog-content {
  padding: 16px 20px 8px;
  .quick-fill-bar {
    margin-bottom: 12px; text-align: center;
  }
  .dialog-field {
    border: 1px solid #e5e5ea; border-radius: 8px; margin-bottom: 10px;
  }
  .switch-row {
    display: flex; justify-content: space-between; align-items: center;
    padding: 4px 6px 10px;
    .switch-label { font-size: 13px; color: #1c1c1e; }
  }
  .manual-hint {
    font-size: 11px; color: #8e8e93; line-height: 1.4; margin-top: 4px;
  }
}

.picker-body { display: flex; flex-direction: column; height: calc(80vh - 60px); }
.student-list { flex: 1; overflow-y: auto; padding: 0 16px; }
.student-item {
  display: flex; align-items: center; justify-content: space-between;
  background: #fff; border-radius: 12px; padding: 12px 14px; margin-bottom: 8px;
  border: 1px solid transparent;
  &.selected { border-color: #007AFF; background: #f0f7ff; }
  .stu-name { font-size: 15px; font-weight: 500; }
  .stu-sn { font-size: 12px; color: #8e8e93; margin-top: 2px; }
  .stu-slot-badge {
    background: #007AFF; color: #fff; font-size: 12px;
    padding: 3px 10px; border-radius: 10px;
  }
}
.picker-footer { padding: 12px 16px calc(12px + env(safe-area-inset-bottom)); }

/* 成绩结算弹窗 */
.result-popup {
  max-height: 80vh; border-radius: 20px 20px 0 0;
  .result-popup-inner {
    display: flex; flex-direction: column; max-height: 80vh;
  }
  .result-header {
    padding: 20px 20px 12px; text-align: center; border-bottom: 1px solid #f2f2f7;
    .result-title {
      display: flex; align-items: center; justify-content: center; gap: 8px;
      font-size: 18px; font-weight: 700; color: #1c1c1e;
    }
    .result-sub {
      font-size: 12px; color: #8e8e93; margin-top: 4px;
    }
  }
  .result-list {
    flex: 1; overflow-y: auto; padding: 16px 20px;
  }
  .result-item-card {
    background: #f8f9fa; border-radius: 12px; padding: 12px 14px; margin-bottom: 10px;
    border: 1px solid #e5e5ea;
    .r-top {
      display: flex; justify-content: space-between; align-items: center;
      .r-slot-name { font-size: 15px; font-weight: 600; color: #1c1c1e; }
      .r-score-wrap {
        display: flex; align-items: baseline; gap: 3px;
        .r-score { font-size: 22px; font-weight: 800; color: #007AFF; }
        .r-unit { font-size: 12px; color: #8e8e93; }
      }
    }
    .r-meta {
      font-size: 12px; color: #8e8e93; margin-top: 4px; display: flex; gap: 10px;
    }
    .r-db-info {
      margin-top: 8px; display: flex; align-items: center; gap: 8px; font-size: 11px;
      .tag-stored {
        background: #e8f8ed; color: #34C759; padding: 2px 6px; border-radius: 4px; font-weight: 600;
      }
      .unique-id {
        color: #8e8e93; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
      }
    }
  }
  .result-actions {
    padding: 12px 20px calc(16px + env(safe-area-inset-bottom));
    border-top: 1px solid #f2f2f7; background: #fff;
    display: flex; flex-direction: column; gap: 8px;
    .btn-action { height: 42px; font-size: 15px; font-weight: 600; }
    .btn-exit { color: #8e8e93; }
  }
}
</style>
